/**
 * Evaluates a string preference as a boolean.
 * Prefs come from CMS as strings. This normalizes the check.
 *
 * @param {string} value - The pref value
 * @param {boolean} [defaultValue=false] - What to return if value is undefined/null
 * @returns {boolean}
 */
export const isPrefEnabled = (value, defaultValue = false) => {
	if (value === undefined || value === null) return defaultValue;
	return value !== 'false';
};

/**
 * Values that explicitly mean "off" for an 'enabled'/'disabled' schema preference.
 * Anything else (including values inherited from another widget's window id, or a
 * future value not yet enumerated) is treated as "on" — see isPrefValueEnabled.
 */
const DISABLING_PREF_VALUES = ['disabled', 'false'];

/**
 * Evaluates an 'enabled'/'disabled' schema preference.
 * Returns false only for an explicitly disabling value, or for absent/null/empty
 * input. Any other non-empty value — including 'enabled', a boolean-style 'true',
 * or a legacy value inherited from a different widget via shared DVS window-id
 * config (e.g. 'first'/'last') — is treated as on. This is deliberate: a widget can
 * inherit a stored preference value it does not itself recognize, and defaulting
 * that case to "off" would silently drop a feature a dealer had already enabled.
 *
 * Use this for prefs that use 'enabled'/'disabled' values.
 * Do NOT use isPrefEnabled for these — isPrefEnabled('disabled') returns true
 * because it checks value !== 'false', not value === 'enabled'.
 *
 * @param {string} value - The pref value
 * @returns {boolean}
 */
export const isPrefValueEnabled = (value) => {
	if (value === undefined || value === null || value === '') return false;
	return !DISABLING_PREF_VALUES.includes(value);
};
